import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import {
  DEFAULT_THEME_BTN_COLOR,
  PRESET_THEME_COLORS,
  getSavedThemeBtnColor,
  saveThemeBtnColor,
  resetThemeBtnColor,
  getSidebarGradient,
  extractThemeColorFromProfile,
} from "../../themeManager";
import { apiEditUserProfile, apiListUserProfile } from "../../api/master";
import "./ThemeColorModal.css";

function ThemeColorModal({ show, onHide }) {
  const [selectedColor, setSelectedColor] = useState(DEFAULT_THEME_BTN_COLOR);
  const [customHex, setCustomHex] = useState(DEFAULT_THEME_BTN_COLOR);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    let isSubscribed = true;
    if (show) {
      const current = getSavedThemeBtnColor();
      setSelectedColor(current);
      setCustomHex(current);

      // Also sync latest profile from backend if user is logged in
      try {
        const inv = JSON.parse(localStorage.getItem("inventory") || "{}");
        const userId = inv?.userData?.user_id;
        if (userId) {
          apiListUserProfile({ user_id: userId })
            .then((res) => {
              if (!isSubscribed) return;
              const serverColor = extractThemeColorFromProfile(res);
              if (
                serverColor &&
                /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(serverColor)
              ) {
                setSelectedColor(serverColor);
                setCustomHex(serverColor);
                saveThemeBtnColor(serverColor);
              }
            })
            .catch((err) => {
              console.warn("apiListUserProfile error in modal:", err);
            });
        }
      } catch (e) {
        console.error(e);
      }
    }
    return () => {
      isSubscribed = false;
    };
  }, [show]);

  const handleSelectColor = (color) => {
    setSelectedColor(color);
    setCustomHex(color);
  };

  const handleCustomHexChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith("#")) {
      val = "#" + val;
    }
    setCustomHex(val);
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val)) {
      setSelectedColor(val);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const inv = JSON.parse(localStorage.getItem("inventory") || "{}");
    const userId = inv?.userData?.user_id || "";

    try {
      // 1. Call API /user/editUserProfile with payload { user_id, theme_color }
      if (userId) {
        await apiEditUserProfile({
          user_id: userId,
          theme_color: selectedColor,
        });
      }

      // 2. Save locally and apply to all components
      saveThemeBtnColor(selectedColor);

      // Keep inventory userData synced
      if (inv?.userData) {
        inv.userData.theme_color = selectedColor;
        localStorage.setItem("inventory", JSON.stringify(inv));
      }

      onHide();

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Theme updated successfully!",
        text: "Theme color saved to your account.",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("apiEditUserProfile error:", error);
      const msg =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update theme profile on server.";

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: msg,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    const inv = JSON.parse(localStorage.getItem("inventory") || "{}");
    const userId = inv?.userData?.user_id || "";

    try {
      if (userId) {
        await apiEditUserProfile({
          user_id: userId,
          theme_color: DEFAULT_THEME_BTN_COLOR,
        });
      }

      resetThemeBtnColor();
      setSelectedColor(DEFAULT_THEME_BTN_COLOR);
      setCustomHex(DEFAULT_THEME_BTN_COLOR);

      if (inv?.userData) {
        inv.userData.theme_color = DEFAULT_THEME_BTN_COLOR;
        localStorage.setItem("inventory", JSON.stringify(inv));
      }

      onHide();

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Reverted to default theme",
        showConfirmButton: false,
        timer: 1600,
      });
    } catch (error) {
      console.error("apiEditUserProfile reset error:", error);
      // Still apply reset locally if user requested
      resetThemeBtnColor();
      setSelectedColor(DEFAULT_THEME_BTN_COLOR);
      setCustomHex(DEFAULT_THEME_BTN_COLOR);
      onHide();
    } finally {
      setIsResetting(false);
    }
  };

  const currentSidebarGradient = getSidebarGradient(selectedColor);

  // Check if current selected color is a custom color (not in presets)
  const isCustomSelected = !PRESET_THEME_COLORS.some(
    (item) => item.value.toLowerCase() === selectedColor.toLowerCase()
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="theme-modal-dialog"
      contentClassName="theme-modal-content"
    >
      <Modal.Header
        closeButton
        className="theme-modal-header"
        style={{ background: currentSidebarGradient }}
      >
        <div>
          <Modal.Title className="h6 fw-bold mb-0 d-flex align-items-center text-white">
            <span className="me-2" style={{ fontSize: "1.1rem" }}>
              🎨
            </span>
            Theme & Appearance Settings
          </Modal.Title>
          <small
            className="text-white text-opacity-75"
            style={{ fontSize: "11px" }}
          >
            Personalize sidebar gradient, action buttons, reports, and modals
          </small>
        </div>
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Active Color & Gradient Banner */}
        <div
          className="d-flex align-items-center justify-content-between p-2 mb-3 rounded-3"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle shadow-sm"
                style={{
                  width: "28px",
                  height: "28px",
                  backgroundColor: selectedColor,
                  border: "2px solid #ffffff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              />
              <div>
                <span
                  className="text-muted d-block text-uppercase fw-semibold"
                  style={{ fontSize: "9px", letterSpacing: "0.5px", lineHeight: "1" }}
                >
                  Selected
                </span>
                <span
                  className="fw-bold font-monospace"
                  style={{ fontSize: "12.5px", color: "#0f172a" }}
                >
                  {selectedColor}
                </span>
              </div>
            </div>

            <div className="vr" style={{ height: "24px", opacity: 0.2 }}></div>

            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-2 shadow-sm"
                style={{
                  width: "36px",
                  height: "24px",
                  background: currentSidebarGradient,
                  border: "1.5px solid #ffffff",
                }}
              />
              <div>
                <span
                  className="text-muted d-block text-uppercase fw-semibold"
                  style={{ fontSize: "9px", letterSpacing: "0.5px", lineHeight: "1" }}
                >
                  Sidebar Gradient
                </span>
                <span
                  className="fw-semibold"
                  style={{ fontSize: "11.5px", color: "#334155" }}
                >
                  135° Harmonized
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-2 py-0"
            style={{ fontSize: "11px", borderRadius: "6px", height: "26px" }}
            onClick={handleReset}
            disabled={isSaving || isResetting}
          >
            {isResetting ? "Resetting..." : "Reset Default"}
          </button>
        </div>

        {/* Curated Color Palettes + Integrated Custom Picker */}
        <div>
          <label
            className="form-label fw-bold mb-2"
            style={{ fontSize: "12px", color: "#1e293b" }}
          >
            Color Palettes & Custom Picker
          </label>
          <div className="row g-2">
            {PRESET_THEME_COLORS.map((item) => {
              const isSelected =
                selectedColor.toLowerCase() === item.value.toLowerCase();
              return (
                <div key={item.value} className="col-6 col-sm-3">
                  <div
                    className={`theme-color-card ${isSelected ? "active" : ""}`}
                    onClick={() => handleSelectColor(item.value)}
                    role="button"
                    style={{
                      borderColor: isSelected ? selectedColor : "#e2e8f0",
                      background: isSelected ? "#f8fafc" : "#ffffff",
                    }}
                    title={`${item.name} (${item.value})`}
                  >
                    <div
                      className="theme-color-circle"
                      style={{ backgroundColor: item.value }}
                    >
                      {isSelected ? "✓" : ""}
                    </div>
                    <div className="text-truncate">
                      <div
                        className="fw-semibold text-truncate"
                        style={{
                          fontSize: "11px",
                          color: isSelected ? selectedColor : "#1e293b",
                          lineHeight: "1.2",
                        }}
                      >
                        {item.name}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Integrated Custom Color Card with Picker */}
            <div className="col-6 col-sm-3">
              <div
                className={`theme-color-card theme-custom-card ${isCustomSelected ? "active" : ""}`}
                style={{
                  borderColor: isCustomSelected ? selectedColor : "#cbd5e1",
                  background: isCustomSelected ? "#f8fafc" : "#ffffff",
                }}
                title="Click to pick any custom color"
              >
                <div
                  className={`theme-color-circle ${isCustomSelected ? "" : "theme-custom-circle"}`}
                  style={{
                    backgroundColor: isCustomSelected ? selectedColor : undefined,
                  }}
                >
                  {isCustomSelected ? "✓" : "🎨"}
                </div>
                <div className="text-truncate">
                  <div
                    className="fw-semibold text-truncate"
                    style={{
                      fontSize: "11px",
                      color: isCustomSelected ? selectedColor : "#1e293b",
                      lineHeight: "1.2",
                    }}
                  >
                    Custom
                  </div>
                </div>
                <input
                  type="color"
                  className="theme-native-color-picker"
                  value={selectedColor}
                  onChange={(e) => handleSelectColor(e.target.value)}
                  title="Choose any custom color"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Slim Hex Code Input */}
        <div
          className="d-flex align-items-center justify-content-between p-2 mt-3 rounded-3"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "11.5px", fontWeight: "600", color: "#475569" }}>
              Hex Code:
            </span>
            <input
              type="text"
              className="form-control form-control-sm text-uppercase fw-semibold font-monospace"
              placeholder="#4263EB"
              maxLength={7}
              value={customHex}
              onChange={handleCustomHexChange}
              style={{
                width: "110px",
                height: "28px",
                fontSize: "12px",
                letterSpacing: "0.5px",
                padding: "2px 8px",
              }}
            />
          </div>
          <small className="text-muted" style={{ fontSize: "11px" }}>
            Type any code (e.g. #D4AF37) or click 🎨 Custom
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light px-3 py-2 border-0 d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleReset}
          disabled={isSaving || isResetting}
          style={{
            fontSize: "11.5px",
            borderRadius: "6px",
            padding: "5px 12px",
            color: "#475569",
          }}
        >
          {isResetting ? "Resetting..." : "Reset to Default"}
        </button>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-light btn-sm border"
            onClick={onHide}
            disabled={isSaving || isResetting}
            style={{
              fontSize: "11.5px",
              borderRadius: "6px",
              padding: "5px 14px",
              color: "#475569",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm text-white shadow d-flex align-items-center gap-1"
            onClick={handleSave}
            disabled={isSaving || isResetting}
            style={{
              backgroundColor: selectedColor,
              borderColor: selectedColor,
              borderRadius: "6px",
              padding: "5px 18px",
              fontSize: "11.5px",
              fontWeight: "600",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              opacity: isSaving ? 0.75 : 1,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
          >
            {isSaving && (
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
                style={{ width: "12px", height: "12px", borderWidth: "2px" }}
              />
            )}
            {isSaving ? "Saving..." : "Save & Apply Theme"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ThemeColorModal;
